/**
 * #1184 — which historical account record is open on a surface, fenced to the
 * viewer that opened it.
 *
 * A record is a read of the CURRENT authorized snapshot, never a copy: the
 * surface resolves `selectedKey` against its own rows on every render, and a
 * key that no longer resolves closes the record (the owning row left).
 *
 * Viewer fence. The record closes when the auth user/org/role/session, the
 * admin's selected organization, the account key mode or the account
 * connection changes — the same events Position Tracker's `clearSelection`
 * closes its panel on. Two layers, deliberately:
 * - store subscriptions fire synchronously on every write, so an A→B→A
 *   organization switch closes the record even though the end state equals
 *   the start;
 * - `viewerKey` is captured at open and compared at render, so even a render
 *   that races the subscription never shows another viewer's record. It folds
 *   in the auth-session and historical-identity epochs, which only ever
 *   increase, so an A-B-A switch can never compare equal.
 * Only a user click opens a record, so a late asynchronous result (a realized
 * read, saved metadata) has no path to reopen one — it can only repaint a
 * record that is still open for the same viewer.
 *
 * Focus returns to the element that opened the record, when it is still in the
 * document. Nothing here reads or writes the hotkey aim (`lt2FocusStore`): the
 * dialog's `aria-modal` root already pauses hotkeys while open.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { historicalIdentityEpoch } from '@/stores/accountMarketIdentity';
import { useAccountStore } from '@/stores/accountStore';
import { useOrgScope } from '@/stores/orgScope';

/** Everything that names "who is looking". Exported for the tests. */
export function currentViewerKey(): string {
  const { status, user } = useAuthStore.getState();
  const adminOrg = user?.role === 'admin' ? useOrgScope.getState().selectedOrgId : '';
  // The auth flag is `unauthenticated`, not the raw status, and the account
  // connection status is left out (a record opened before connect survives
  // connecting; a drop to `disconnected` closes it below). The historical-
  // identity epoch is deliberately IN: identity retention bumps it on every
  // auth status change, including a same-user `loading` flicker, so the record
  // never outlives the saved-label session it was read in (#1184 review).
  return JSON.stringify([
    getAuthSessionEpoch(), historicalIdentityEpoch(), status === 'unauthenticated', user?.id ?? null,
    user?.orgId ?? null, user?.role ?? null, adminOrg, useAccountStore.getState().keyMode,
  ]);
}

interface Selection { key: string; viewerKey: string }

export interface AccountRecordSelection {
  /** The open record's row key, or null. Null whenever the viewer changed. */
  selectedKey: string | null;
  /** Open `key`; `trigger` receives focus back on close. */
  open: (key: string, trigger?: HTMLElement | null) => void;
  close: () => void;
}

export function useAccountRecord(): AccountRecordSelection {
  const [selection, setSelection] = useState<Selection | null>(null);
  const trigger = useRef<HTMLElement | null>(null);

  const open = useCallback((key: string, from?: HTMLElement | null) => {
    trigger.current = from ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    setSelection({ key, viewerKey: currentViewerKey() });
  }, []);
  const close = useCallback(() => setSelection(null), []);

  useEffect(() => {
    if (!selection) return;
    // Field checks catch a change even where the end state equals the start
    // (A→B→A); the key check catches what only the epochs record — e.g.
    // identity retention resetting the saved-label session on an auth flicker
    // (its auth listener is registered at boot, so it has run by now).
    const keyMoved = () => currentViewerKey() !== selection.viewerKey;
    const offAuth = useAuthStore.subscribe((next, previous) => {
      if ((next.status === 'unauthenticated') !== (previous.status === 'unauthenticated')
        || next.user?.id !== previous.user?.id
        || next.user?.orgId !== previous.user?.orgId || next.user?.role !== previous.user?.role
        || keyMoved()) close();
    });
    // The selected organization scopes only an admin's view (App.tsx), so it
    // is part of the viewer key for admins alone — close on it the same way.
    const offOrg = useOrgScope.subscribe((next, previous) => {
      if (next.selectedOrgId !== previous.selectedOrgId && useAuthStore.getState().user?.role === 'admin') close();
    });
    const offAccount = useAccountStore.subscribe((next, previous) => {
      if (next.keyMode !== previous.keyMode
        || (next.status === 'disconnected' && previous.status !== 'disconnected') || keyMoved()) close();
    });
    return () => { offAuth(); offOrg(); offAccount(); };
  }, [selection, close]);

  // Return focus once the record has unmounted.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (selection) { wasOpen.current = true; return; }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const el = trigger.current;
    trigger.current = null;
    if (el?.isConnected) el.focus({ preventScroll: true });
  }, [selection]);

  const stale = selection != null && selection.viewerKey !== currentViewerKey();
  // Hidden this render already; the close also drops the selection so the
  // focus-return path runs and nothing of the old viewer's record survives.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (stale) close(); }, [stale, close]);
  return { selectedKey: selection && !stale ? selection.key : null, open, close };
}

/** The open record's row, resolved from the surface's current snapshot. A
 *  selected key that no longer resolves (the row left the read) closes the
 *  record rather than showing a remembered copy. */
export function useOpenRecordRow<T>(selection: AccountRecordSelection, resolve: (key: string) => T | null): T | null {
  const row = selection.selectedKey != null ? resolve(selection.selectedKey) : null;
  const gone = selection.selectedKey != null && row == null;
  const { close } = selection;
  // Must CLOSE, not just hide: a row that later reappears under the same key
  // would otherwise reopen the record without a click.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (gone) close(); }, [gone, close]);
  return row;
}
